import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";

import { Card, CardVariant } from "../../ui/Cards/Card";
import DraggableItem from "../DragAndDrop/DraggableItem";
import { useContextualActions } from "../hooks/useContextualActions";
import { CONTENT_ENTER_ANIMATION_CLASSNAME } from "../hooks/useInitialEnterAnimation";
import { useItemSelection } from "../hooks/useItemSelection";
import { useSmartTableNavigationPrefetch } from "../hooks/useSmartTableNavigationPrefetch";
import { SelectionIndicator } from "../SelectionIndicator";
import { RegistryItem } from "../types";
import { useUnifiedRegistry } from "../unified-registry-context";
import { getItemLink } from "../utils";

import { EntityScreenshotPreview } from "./EntityScreenshotPreview";

import EntityFavoriteStar from "@/components/EntityFavoriteStar";
import { EntityIcon } from "@/components/ui/icons";
import { useUser } from "@/context/user-context";
import { cn } from "@/lib/utils";
import { EntityType } from "@/queries/folder-entities/types";
import { withPreservedWorkspaceSearch } from "@/utils/requestLogUrlParams";

interface CardRouterProps {
  card: RegistryItem;
  onItemClick?: (item: RegistryItem) => void;
  shouldAnimateIn?: boolean;
}

const CardRouter = ({ card: item, onItemClick, shouldAnimateIn = false }: CardRouterProps) => {
  const workspaceId = useUser().activeWorkspaceId!;
  const location = useLocation();
  const registry = useUnifiedRegistry();
  const { handleItemClick } = useItemSelection(onItemClick);
  const { actions, isSelected: selected } = useContextualActions(item);

  const isDisabled = registry.isDisabled(item);

  const itemLink = useMemo(
    () =>
      onItemClick
        ? "#"
        : withPreservedWorkspaceSearch(getItemLink(item, workspaceId), location.search),
    [item, location.search, onItemClick, workspaceId],
  );
  const prefetchSmartTableNavigation = useSmartTableNavigationPrefetch({
    item,
    workspaceId,
  });

  const baseProps = {
    item,
    link: itemLink,
    title: item.name,
    subtitle:
      item.type === EntityType.FOLDER && "itemCount" in item && item.itemCount !== undefined
        ? `${item.itemCount} item${item.itemCount !== 1 ? "s" : ""}`
        : undefined,
    icon: <EntityIcon type={item.type} />,
    actions,
    useLink: !onItemClick,
  };

  const handleWrapperClick = (e: React.MouseEvent) => {
    if (isDisabled) {
      return; // Prevent any interaction with disabled items
    }

    // Handle multi-select first, before the Link component gets the event
    const wasHandled = handleItemClick(e, item);
    if (wasHandled) {
      // Multi-select was handled, don't propagate to Link
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    // For regular clicks, let the Link component handle navigation
    // unless we have a custom onItemClick handler
    if (onItemClick) {
      e.preventDefault();
      e.stopPropagation();
      onItemClick(item);
    }
  };

  const getCardVariant = () => {
    if (isDisabled) {
      return CardVariant.DISABLED;
    }
    if (selected) {
      return CardVariant.SELECTED;
    }
    return CardVariant.DEFAULT;
  };

  return (
    <DraggableItem item={item} className="h-full" disabled={isDisabled}>
      <div
        className={cn("relative h-full transition-all duration-200")}
        onClick={handleWrapperClick}
        onPointerEnter={prefetchSmartTableNavigation}
        onFocus={prefetchSmartTableNavigation}
        onMouseDown={prefetchSmartTableNavigation}
      >
        <SelectionIndicator isSelected={selected} />
        <Card
          modal={registry.modal}
          contentClassName={shouldAnimateIn ? CONTENT_ENTER_ANIMATION_CLASSNAME : undefined}
          variant={getCardVariant()}
          {...baseProps}
          disabled={isDisabled}
          onClick={undefined} // Remove Card's onClick to prevent conflicts
          headerActions={
            !isDisabled && !registry.modal ? (
              <EntityFavoriteStar entityType={item.type} entityId={item.id} />
            ) : undefined
          }
          body={
            item.type !== EntityType.FOLDER ? (
              <EntityScreenshotPreview item={item} workspaceId={workspaceId} />
            ) : undefined
          }
        />
      </div>
    </DraggableItem>
  );
};

export default observer(CardRouter);
